import { create } from "zustand";
import { persist } from "zustand/middleware";

const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

export interface CampaignAttribution {
  source?: string;
  medium?: string;
  campaign?: string;
  gclid?: string;
  fbclid?: string;
  landingPage?: string;
  referrer?: string;
  capturedAt: number;
}

interface AttributionStoreState {
  firstTouch: CampaignAttribution | null;
  lastTouch: CampaignAttribution | null;
  recordAttribution: (attribution: CampaignAttribution) => void;
  clearAttribution: () => void;
}

export const useAttributionStore = create<AttributionStoreState>()(
  persist(
    (set, get) => ({
      firstTouch: null,
      lastTouch: null,
      recordAttribution: (attribution) => {
        const state = get();
        const isFirstExpired =
          !state.firstTouch ||
          Date.now() - state.firstTouch.capturedAt > MAX_AGE_MS;
        set({
          firstTouch: isFirstExpired ? attribution : state.firstTouch,
          lastTouch: attribution,
        });
      },
      clearAttribution: () => set({ firstTouch: null, lastTouch: null }),
    }),
    {
      name: "thecoach_campaign_attribution",
    },
  ),
);

function normalize(value: string | null) {
  const trimmed = value?.trim();
  return trimmed ? trimmed.slice(0, 300) : undefined;
}

export function captureCampaignAttribution() {
  if (typeof window === "undefined") return;

  const params = new URLSearchParams(window.location.search);
  const gclid = normalize(params.get("gclid"));
  const fbclid = normalize(params.get("fbclid"));
  const explicitSource = normalize(params.get("utm_source"));
  const source =
    explicitSource || (gclid ? "google" : fbclid ? "meta" : undefined);
  const medium =
    normalize(params.get("utm_medium")) ||
    (gclid || fbclid ? "paid" : undefined);
  const campaign = normalize(params.get("utm_campaign"));

  if (!source && !medium && !campaign && !gclid && !fbclid) return;

  const attribution: CampaignAttribution = {
    source,
    medium,
    campaign,
    gclid,
    fbclid,
    landingPage: `${window.location.pathname}${window.location.search}`,
    referrer: normalize(document.referrer),
    capturedAt: Date.now(),
  };

  useAttributionStore.getState().recordAttribution(attribution);
}

export function readCampaignAttribution(): CampaignAttribution | null {
  const { lastTouch, firstTouch } = useAttributionStore.getState();
  if (lastTouch && Date.now() - lastTouch.capturedAt <= MAX_AGE_MS) {
    return lastTouch;
  }
  if (firstTouch && Date.now() - firstTouch.capturedAt <= MAX_AGE_MS) {
    return firstTouch;
  }
  return null;
}

export function clearCampaignAttribution() {
  useAttributionStore.getState().clearAttribution();
}
